/**
 * Browser history integration for product-grid2.
 *
 * This module provides two-way synchronization between Redux state and the browser's
 * URL query string, enabling:
 * - Shareable URLs with active filters
 * - Bookmarkable product grid states
 * - Browser back/forward navigation with filter preservation
 *
 * ## How it Works
 *
 * 1. **State to URL**: When filters change, `pushFilterStateChange` updates the URL query string
 * 2. **URL to State**: When users navigate (back/forward), the history listener updates Redux
 * 3. **Initial Load**: `getFilterState` (filterQueryString.ts) reads query params
 *
 ## Important Notes
 *
 * - Only filter selections are synchronized, not UI state like filter visibility
 * - History pushes are synchronous side effects called from reducers (unconventional but intentional)
 * - This pattern prioritizes user experience (shareable URLs) over strict Redux patterns
 * - The base query string is preserved (params present at page load aren't overwritten)
 *
 * @see reducers.ts for where pushFilterStateChange is called as a side effect
 * @see filterQueryString.ts for the read side (parsing the query string)
 */
import { Dispatch } from "@reduxjs/toolkit";
import { createBrowserHistory, createMemoryHistory } from "history";
import { type WritableDraft, current, freeze, isDraft } from "immer";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { applyHistoryUpdate } from "./reducers";
import { IReduxState } from "./reducers.interfaces";

interface IHistoryState {
    filters?: IReduxState["filters"];
}

// Set-up history API
const window = getWindow();
const baseURL = window ? window.location.pathname : "";
const baseQueryString = window
    ? new URLSearchParams(window.location.search.replace(/^\?/, "")).toString()
    : "";

const history = window
    ? createBrowserHistory({
          basename: baseURL,
          forceRefresh: false,
      })
    : createMemoryHistory({ initialEntries: ["/"] });

/**
 * Starts listening to browser history changes and syncs them to Redux.
 * Should be called once during app initialization.
 *
 * When users navigate using browser back/forward buttons, this listener
 * dispatches an action to update Redux state to match the URL.
 */
export const startHistoryListener = (dispatch: Dispatch): void => {
    history.listen((location, action) => {
        // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        const state: IHistoryState | undefined | null = location.state as any;
        if (action !== "PUSH" && state && state.filters) {
            // Update Redux to match the new history location
            dispatch(applyHistoryUpdate(state.filters));
        }
    });
};

/**
 * Updates the URL query string to reflect current filter state.
 * Called as a side effect from filter update reducers.
 *
 * This function:
 * 1. Preserves base query params that were present at page load
 * 2. Updates filter params to match current selections
 * 3. Removes params for filters with no selections
 * 4. Pushes a new history entry (enables back/forward navigation)
 *
 * Note: This is called synchronously from reducers, which is unconventional
 * but necessary for seamless URL synchronization.
 *
 * @param filters - Current filter state to sync to URL
 */
export const pushFilterStateChange = (
    filtersOrDraft:
        | IReduxState["filters"]
        | WritableDraft<IReduxState["filters"]>,
): void => {
    const filters = isDraft(filtersOrDraft)
        ? freeze(current(filtersOrDraft), true)
        : filtersOrDraft;

    // Start with the base params (present at the time of page load)
    const params = new URLSearchParams(baseQueryString);
    // Build query string params for current filter state
    for (const filterID of Object.keys(filters)) {
        if (
            filters[filterID].selectedValues &&
            filters[filterID].selectedValues.length > 0
        ) {
            params.set(filterID, filters[filterID].selectedValues.join(","));
        } else {
            params.delete(filterID);
        }
    }
    // Filter out empty keys
    for (const key of params.keys()) {
        if (!key || !params.get(key)) {
            params.delete(key);
        }
    }
    // Push history entry
    const historyState: IHistoryState = {
        filters: filters,
    };
    const entry = {
        search: `?${params.toString()}`,
        state: historyState,
    };
    history.push(entry);
};
